import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router';

import { buildLoginUrl } from '@/api/auth-state';
import Loading from '@/components/atoms/Loading/Loading';
import { loadUser } from '@/redux/user/actions';
import { selectIsAuthenticated, selectIsLoadingUser } from '@/redux/user/selectors';
import { RootState } from '@/shared/types/state';

/**
 * Route-level authentication guard component.
 * Checks authentication status and redirects to login if not authenticated.
 * Shows loading state during auth verification.
 * Uses Outlet to render child routes when authenticated.
 */
export function RequireAuth() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoadingUser = useSelector(selectIsLoadingUser);
  const loginStatus = useSelector((state: RootState) => state.user?.loginStatus);

  // Use ref to track if we've initiated loading to prevent duplicate dispatches
  const loadInitiated = useRef(false);

  const hasLoginAttempted = loginStatus?.loaded || loginStatus?.error;

  useEffect(() => {
    // Load user data if not already loaded and not currently loading
    // Use ref to ensure we only dispatch once even in StrictMode
    if (!isAuthenticated && !isLoadingUser && !hasLoginAttempted && !loadInitiated.current) {
      loadInitiated.current = true;
      dispatch(loadUser());
    }
  }, [dispatch, isAuthenticated, isLoadingUser, hasLoginAttempted]);

  // 401 → redirect is handled centrally in fetcher.ts/client.ts via
  // handleAuthFailure. By the time loginStatus.error reaches Redux, the
  // page is already navigating away on session-death codes — no useEffect
  // needed here. The Navigate fallback below handles the route-state case
  // where loadUser hasn't been attempted yet.

  // Determine the current state and render accordingly
  // Priority: Loading > Authenticated > Failed > Initial

  // If we're loading or about to load (initial state), show loading
  if (isLoadingUser || (!isAuthenticated && !hasLoginAttempted)) {
    return <Loading label={t('common.loading')} />;
  }

  // If authenticated, render children
  if (isAuthenticated) {
    return <Outlet />;
  }

  // If not authenticated and we've tried to load, redirect to login
  if (hasLoginAttempted) {
    return <Navigate to={buildLoginUrl(location.pathname + location.search)} replace />;
  }

  // This should never be reached, but include as safety fallback
  return <Loading label={t('common.loading')} />;
}

export default RequireAuth;
